/**
 * CalculatorPage.jsx — Revenue Split Calculator (Budget-Owner Model)
 *
 * Three department types:
 *   - Originator: absolute % splits, remainder → Company
 *   - Production: budget-owner (1 lead + N hourly), overrun → Company
 *   - Post Production: budget-owner (1 lead + N hourly), overrun → Company
 *
 * Features:
 *   - Job selector (pre-fill revenue, load existing snapshot)
 *   - Revenue-scaling formula (autoScale) or manual splits
 *   - Empty department redistribution (prod↔post, both→company)
 *   - Crew picker from roster with rate auto-fill from settings
 *   - AutoEqual for originators
 *   - Estimated hours reference, actual hours from time_entries
 *   - Overrun warnings (hourly always paid in full)
 *   - Save merges with existing snapshot (preserves quote keys)
 *
 * Spec: docs/PAY_CALCULATOR_V2_PROPOSAL.md
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { navigateToJob } from '../lib/crossNav';
import { useSettings } from '../hooks/useSettings';
import { useJobs, useCreateJob, useUpdateJob } from '../hooks/useJobs';
import { useRoster } from '../hooks/useRoster';
import { useTimeEntriesByJob } from '../hooks/useTimeEntries';
import { useExpensesByJob } from '../hooks/useExpenses';
import { useToast } from '../components/layout/Toast';
import { useContacts } from '../hooks/useContacts';
import { useRBAC } from '../hooks/useRBAC';
import { useAuth } from '../hooks/useAuth';
import { useFocus } from '../hooks/useFocus';
import { fmtC, cn, uid } from '../lib/utils';
import { api } from '../lib/api';
import { getCrewRate } from '../lib/crewConstants';
import UserAvatar from '../components/shared/UserAvatar';
import FocusHide from '../components/shared/FocusHide';
import { getUserColor } from '../lib/userColors';
import {
  calculateSplits,
  redistributeEmpty,
  calculateOriginatorEarnings,
  autoEqualOriginators,
  calculateDeptEarnings,
  buildSnapshot,
  normalizeSnapshot,
  applyExpenseDeductions,
  itemLines,
  buildItemPieces,
  computeItemsAllocation,
  applyItemsExpenseDeductions,
  buildItemsSnapshot,
  computeStaycationSplit,
  buildStaycationSnapshot,
} from '../lib/calcEngine';

const QUICK_AMOUNTS = [1500, 2500, 5000, 7500, 10000, 15000, 25000];

// Parse roster name (handles V1 nested JSON)
function parseRosterName(raw) {
  if (!raw || typeof raw !== 'string') return raw || 'Unknown';
  let parsed = raw;
  for (let i = 0; i < 3; i++) {
    try {
      const attempt = JSON.parse(parsed);
      if (typeof attempt === 'object' && attempt !== null && attempt.name) {
        parsed = attempt.name;
      } else if (typeof attempt === 'string') {
        parsed = attempt;
      } else break;
    } catch { break; }
  }
  return parsed;
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

// ── Split dialog — sub-divide one line item into pay pieces ──────────────
// Amounts must sum to the line total (client never sees these; internal only)
function SplitDialog({ line, existing, onSave, onRemove, onClose }) {
  const lineAmount = round2((parseFloat(line.qty) || 0) * (parseFloat(line.rate) || 0) || parseFloat(line.amount) || 0);
  const [rows, setRows] = useState(() => (
    existing?.length
      ? existing.map(r => ({ ...r }))
      : [
          { id: 'si_' + uid(), desc: 'Shoot', amount: round2(lineAmount / 2) },
          { id: 'si_' + uid(), desc: 'Edit', amount: round2(lineAmount - round2(lineAmount / 2)) },
        ]
  ));

  const sum = round2(rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0));
  const balanced = Math.abs(sum - lineAmount) <= 0.01;

  const setRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setRows(prev => [...prev, { id: 'si_' + uid(), desc: '', amount: 0 }]);
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
  const autoBalance = () => {
    setRows(prev => {
      if (!prev.length) return prev;
      const others = prev.slice(0, -1).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
      return prev.map((r, i) => (
        i === prev.length - 1 ? { ...r, amount: Math.max(0, round2(lineAmount - others)) } : r
      ));
    });
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-full max-w-md bg-surface-alt border border-border rounded-lg p-4 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-sm font-heading text-text-primary mb-1">Split item</h3>
        <p className="text-2xs text-text-dim mb-3">
          {line.desc} — {fmtC(lineAmount)}. Pieces are internal (never shown to the client);
          each can carry its own lead.
        </p>

        <div className="space-y-2 mb-3">
          {rows.map((r, idx) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={r.desc}
                onChange={e => setRow(idx, 'desc', e.target.value)}
                placeholder="Piece name (e.g. Shoot day)"
                className="flex-1 min-w-[120px] px-2 py-1.5 bg-surface border border-border rounded text-xs text-text-primary focus:outline-none focus:border-accent"
              />
              <div className="flex items-center gap-1">
                <span className="text-2xs text-text-dim">$</span>
                <input
                  type="number"
                  value={r.amount}
                  onChange={e => setRow(idx, 'amount', e.target.value)}
                  min="0"
                  step="25"
                  className="w-24 px-2 py-1.5 bg-surface border border-border rounded text-xs text-text-primary text-right focus:outline-none focus:border-accent"
                />
              </div>
              <button
                onClick={() => removeRow(idx)}
                className="text-red-accent text-2xs hover:bg-red-accent/10 px-1.5 py-1 rounded transition-colors"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={addRow} className="px-2 py-1 bg-accent/10 border border-accent/30 rounded text-2xs text-accent hover:bg-accent/20 transition-colors">
            + Piece
          </button>
          <button onClick={autoBalance} className="px-2 py-1 bg-surface border border-border rounded text-2xs text-text-muted hover:border-accent/50 transition-colors">
            Balance last
          </button>
          <span className={cn('ml-auto text-2xs font-semibold', balanced ? 'text-green' : 'text-red-accent')}>
            {fmtC(sum)} / {fmtC(lineAmount)}
          </span>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          {existing?.length > 0 && (
            <button
              onClick={onRemove}
              className="mr-auto px-3 py-1.5 text-xs text-red-accent border border-red-accent/30 rounded hover:bg-red-accent/10 transition-colors"
            >
              Remove split
            </button>
          )}
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-text-dim border border-border rounded hover:bg-surface transition-colors">
            Cancel
          </button>
          <button
            onClick={() => onSave(rows)}
            disabled={!balanced || rows.length < 2}
            className="px-3 py-1.5 text-xs font-semibold text-bg bg-accent rounded hover:bg-accent-hover disabled:opacity-50 transition-colors"
          >
            Apply split
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CalculatorPage({ embedded = false }) {
  const [amount, setAmount] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [autoScale, setAutoScale] = useState(true);
  const [manualSplits, setManualSplits] = useState({ company: 0, orig: 0, prod: 0, post: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: settings } = useSettings();
  const { data: allJobs = [] } = useJobs();
  const { data: roster = [] } = useRoster();
  const createJob = useCreateJob();
  const updateJob = useUpdateJob();
  const { toast } = useToast();
  const { isHidden } = useFocus();

  // Originator section
  const [originators, setOriginators] = useState([]);

  // Production section
  const [prodLead, setProdLead] = useState(null);
  const [prodHourly, setProdHourly] = useState([]);

  // Post section
  const [postLead, setPostLead] = useState(null);
  const [postHourly, setPostHourly] = useState([]);

  // ── Items mode (per-line-item pay allocation) ──
  // payMode 'items': each quote line (or sub-piece) carries its own lead(s);
  // 'staycation': segment-based hourly plan with a company profit target;
  // 'legacy': job-level prod/post departments (the original model)
  const [payMode, setPayMode] = useState('legacy');
  // Staycation mode: company target ($, canonical), hrs/segment, crew rows
  const [stayCompanyTarget, setStayCompanyTarget] = useState('');
  const [stayHrsPerSegment, setStayHrsPerSegment] = useState(12);
  // [{ rosterId, name, crewId, rate, segments, extraHours }]
  const [stayRows, setStayRows] = useState([]);
  // Mode to return to when leaving staycation ('legacy' | 'items')
  const [preStayMode, setPreStayMode] = useState('legacy');
  // { [pieceKey]: { shootLead, editLead, soloLead, shootHourly:[], editHourly:[] } }
  const [itemAssign, setItemAssign] = useState({});
  // { [lineSid]: [{ id, desc, amount }] } — calculator-owned sub-division
  const [itemSplits, setItemSplits] = useState({});
  const [splitDialogSid, setSplitDialogSid] = useState(null);
  const [orphanedAllocs, setOrphanedAllocs] = useState([]);
  // Job-level crew as it stood when per-item was last left (unsaved round trip)
  const jobLevelAtLeaveRef = useRef(null);
  // Freshest updated_at seen for the selected job (optimistic concurrency echo)
  const [jobUpdatedAt, setJobUpdatedAt] = useState(null);

  // UI state for crew pickers
  const [activePicker, setActivePicker] = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');

  // New-job dialog state
  const [showNewJobDialog, setShowNewJobDialog] = useState(false);
  const [newJobTitle, setNewJobTitle] = useState('');
  const [newJobContact, setNewJobContact] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);

  // Time entries for selected job
  const { data: jobTimeEntries = [] } = useTimeEntriesByJob(selectedJobId || undefined);

  // Job expenses for expense deduction — grouped by department
  const { data: jobExpenses = [] } = useExpensesByJob(selectedJobId || undefined);
  const expensesByDept = useMemo(() => {
    const result = { orig: 0, prod: 0, post: 0 };
    for (const e of jobExpenses) {
      const dept = (e.department || 'production').toLowerCase();
      const amt = parseFloat(e.amount) || 0;
      if (dept === 'company') continue; // company-dept excluded from engine
      if (dept === 'originator') result.orig += amt;
      else if (dept === 'post_production') result.post += amt;
      else result.prod += amt; // default to production
    }
    result.orig = Math.round(result.orig * 100) / 100;
    result.prod = Math.round(result.prod * 100) / 100;
    result.post = Math.round(result.post * 100) / 100;
    return result;
  }, [jobExpenses]);
  const totalJobExpenses = expensesByDept.orig + expensesByDept.prod + expensesByDept.post;

  const { data: contacts = [] } = useContacts();

  // settings from useSettings: { data: { splitCompany, crewRates, ... } }
  const settingsData = useMemo(() => settings?.data || {}, [settings]);

  // Contacts sorted, filterable by search (clients + vendors)
  const sortedContacts = useMemo(
    () => contacts
      .filter((c) => (c.type === 'client' || c.type === 'vendor') && c.name)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [contacts]
  );

  const filteredContacts = useMemo(() => {
    if (!clientSearch) return sortedContacts;
    const q = clientSearch.toLowerCase();
    return sortedContacts.filter(
      (c) => c.name.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q)
    );
  }, [sortedContacts, clientSearch]);

  // ── Init manual splits from settings defaults ─────────────────────────
  useEffect(() => {
    if (!settingsData.splitCompany) return;
    setManualSplits({
      company: parseFloat(settingsData.splitCompany) || 0,
      orig: parseFloat(settingsData.splitOriginator) || 0,
      prod: parseFloat(settingsData.splitProduction) || 0,
      post: parseFloat(settingsData.splitPostProduction) || 0,
    });
  }, [settingsData]);

  // ── URL params: ?amount=X or ?jobId=X ─────────────────────────────────
  useEffect(() => {
    const urlAmount = searchParams.get('amount');
    const urlJobId = searchParams.get('jobId');
    if (urlAmount) setAmount(urlAmount);
    if (urlJobId) setSelectedJobId(urlJobId);
    if (urlAmount || urlJobId) {
      searchParams.delete('amount');
      searchParams.delete('jobId');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ── Load existing snapshot when job selected ──────────────────────────
  useEffect(() => {
    if (!selectedJobId) return;
    const job = allJobs.find(j => j.id === selectedJobId);
    if (!job) return;

    setJobUpdatedAt(job.updatedAt || null);

    // Auto-advance to shooting if shoot date has passed and status is pre-shooting
    if (['quoted', 'confirmed'].includes(job.status)) {
      const lastShoot = Array.isArray(job.shootDates) && job.shootDates.length > 0
        ? job.shootDates[job.shootDates.length - 1]
        : job.shootDate;
      if (lastShoot && new Date(lastShoot) <= new Date()) {
        updateJob.mutateAsync({ id: job.id, status: 'shooting' })
          .then(res => { if (res?.updatedAt) setJobUpdatedAt(res.updatedAt); })
          .catch(() => {});
      }
    }

    const rev = parseFloat(job.revenue) || 0;
    setAmount(rev > 0 ? String(rev) : '');

    // Reset crew state before loading — prevents stale crew leaking across jobs
    setOriginators([]);
    setProdLead(null);
    setProdHourly([]);
    setPostLead(null);
    setPostHourly([]);
    setPayMode('legacy');
    setItemAssign({});
    setItemSplits({});
    setOrphanedAllocs([]);
    setSplitDialogSid(null);
    jobLevelAtLeaveRef.current = null;
    setStayCompanyTarget('');
    setStayHrsPerSegment(12);
    setStayRows([]);
    setPreStayMode('legacy');

    if (job.calcSnapshot) {
      const snap = normalizeSnapshot(job.calcSnapshot);
      const lines = itemLines(job.calcSnapshot);

      // ── Staycation mode restore ──
      if (job.calcSnapshot.payMode === 'staycation' && job.calcSnapshot.staycation) {
        const st = job.calcSnapshot.staycation;
        setPayMode('staycation');
        setStayCompanyTarget(st.companyTarget > 0 ? String(st.companyTarget) : '');
        // Preserve a saved 0 (segments-tracked-but-hourly-driven plan); only
        // fall back to 12 when the value is genuinely missing/NaN.
        setStayHrsPerSegment(Number.isFinite(parseFloat(st.hrsPerSegment)) ? parseFloat(st.hrsPerSegment) : 12);
        setStayRows((st.rows || []).map(r => ({
          rosterId: r.rosterId || null,
          name: r.name || '',
          crewId: r.crewId || null,
          rate: parseFloat(r.rate) || 0,
          segments: parseInt(r.segments, 10) || 0,
          extraHours: parseFloat(r.extraHours) || 0,
        })));
        return;
      }

      // ── Items mode restore ──
      if (job.calcSnapshot.payMode === 'items') {
        const savedSplits = job.calcSnapshot.itemSplits || {};
        const { pieces } = buildItemPieces(lines, savedSplits);
        const keyOf = new Map(pieces.map(p => [p.key, p]));
        const assign = {};
        const orphans = [];
        const member = (m) => ({
          rosterId: m.rosterId || null, name: m.name || '', crewId: m.crewId || null,
          hourlyRate: m.hourlyRate || 0, estHours: m.estHours || 0,
        });
        const restore = (piece, a) => {
          assign[piece.key] = {
            soloLead: !!a.soloLead,
            shootLead: a.shoot?.lead ? member(a.shoot.lead) : null,
            editLead: a.edit?.lead ? member(a.edit.lead) : null,
            shootHourly: (a.shoot?.hourly || []).map(member),
            editHourly: (a.edit?.hourly || []).map(member),
          };
        };
        // Exact key matches first, then the desc fallback (line renamed or
        // re-added) against lines still unclaimed — so a same-named line (an
        // add-on and a custom line, say) never takes another line's assignment
        const unmatched = (job.calcSnapshot.itemAllocs || []).filter(a => {
          const piece = keyOf.get(a.key);
          if (!piece || assign[piece.key]) return true;
          restore(piece, a);
          return false;
        });
        unmatched.forEach(a => {
          const piece = pieces.find(p => p.desc === a.desc && !assign[p.key]);
          if (!piece) {
            if (a.shoot?.lead || a.edit?.lead) orphans.push(a.desc || a.key);
            return;
          }
          restore(piece, a);
        });
        setPayMode('items');
        setItemSplits(savedSplits);
        setItemAssign(assign);
        setOrphanedAllocs(orphans);
        setAutoScale(snap.autoScale ?? true);
        if (!snap.autoScale && snap.splits) setManualSplits(snap.splits);
        setOriginators((snap.originators || []).map(o => ({
          rosterId: o.rosterId || null,
          name: o.name || '',
          crewId: o.crewId || null,
          pct: o.pct || 0,
        })));
        return;
      }

      // Only restore calculator state if snapshot has actual pay data
      // (not just quote keys like deliverableLines/quoteNumber/mode/sqft)
      const hasPayData = !!(snap.savedAt || snap.originators?.length > 0 || snap.prod?.lead || snap.post?.lead);

      // Fresh job, no pay data yet: staycation-flagged → staycation mode;
      // RE stays job-level (per-item is opt-in there); other jobs with line
      // items → items mode
      const isRE = job.jobType === 'Real Estate' || job.calcSnapshot.mode === 're';
      if (!hasPayData && (job.workflowType === 'staycation' || (lines.length > 0 && !isRE))) {
        setPayMode(job.workflowType === 'staycation' ? 'staycation' : 'items');
        return;
      }

      if (hasPayData) {
        setAutoScale(snap.autoScale ?? true);
        if (!snap.autoScale && snap.splits) {
          setManualSplits(snap.splits);
        }

        setOriginators((snap.originators || []).map(o => ({
          rosterId: o.rosterId || null,
          name: o.name || '',
          crewId: o.crewId || null,
          pct: o.pct || 0,
        })));

        setProdLead(snap.prod?.lead ? {
          rosterId: snap.prod.lead.rosterId || null,
          name: snap.prod.lead.name || '',
          crewId: snap.prod.lead.crewId || null,
        } : null);
        setProdHourly((snap.prod?.hourly || []).map(m => ({
          rosterId: m.rosterId || null,
          name: m.name || '',
          crewId: m.crewId || null,
          hourlyRate: m.hourlyRate || 0,
          estHours: m.estHours || 0,
        })));

        setPostLead(snap.post?.lead ? {
          rosterId: snap.post.lead.rosterId || null,
          name: snap.post.lead.name || '',
          crewId: snap.post.lead.crewId || null,
        } : null);
        setPostHourly((snap.post?.hourly || []).map(m => ({
          rosterId: m.rosterId || null,
          name: m.name || '',
          crewId: m.crewId || null,
          hourlyRate: m.hourlyRate || 0,
          estHours: m.estHours || 0,
        })));
      }
    } else if (job.workflowType === 'staycation') {
      // Staycation-flagged job with no snapshot yet (e.g. created directly on
      // the Jobs page) → default straight into staycation mode.
      setPayMode('staycation');
    }
  }, [selectedJobId, allJobs]);

  // ── VirTour jobs: seed Cyprian as the originator ──────────────────────
  // VirTour work is always originated on Cyprian's relationship, and the quote
  // snapshot carries no originators. Seeded once per job (and only when the
  // restore left the list empty), since the roster can resolve after the job.
  const vtSeededJobRef = useRef(null);
  useEffect(() => {
    if (!selectedJobId || roster.length === 0) return;
    if (vtSeededJobRef.current === selectedJobId) return;
    const job = allJobs.find(j => j.id === selectedJobId);
    if (!job) return;
    if (job.jobType !== 'VirTour' && job.calcSnapshot?.mode !== 'virtour') return;
    vtSeededJobRef.current = selectedJobId;
    const rm = roster.find(r => parseRosterName(r.name).toLowerCase().includes('cyprian'));
    if (!rm) return;
    setOriginators(prev => prev.length > 0 ? prev : [{
      rosterId: rm.id,
      name: parseRosterName(rm.name),
      crewId: rm.crewId || null,
      pct: 100,
    }]);
  }, [selectedJobId, allJobs, roster]);

  const numericAmount = parseFloat(amount) || 0;

  // ── Compute splits ────────────────────────────────────────────────────
  const splits = useMemo(() => {
    if (autoScale) return calculateSplits(numericAmount, settingsData, true);
    return manualSplits;
  }, [autoScale, numericAmount, settingsData, manualSplits]);

  const totalPct = round2(splits.company + splits.orig + splits.prod + splits.post);

  // ── Items mode: pieces + allocation pipeline ──────────────────────────
  const selectedJob = useMemo(
    () => allJobs.find(j => j.id === selectedJobId) || null,
    [allJobs, selectedJobId]
  );

  // "Transparent to assigned": crew see the company cut/margin ONLY for jobs
  // they're assigned to (admins always). Hidden for non-assigned / blank quotes.
  const { isAdmin } = useRBAC();
  const { profile } = useAuth();
  const canSeeCompany = useMemo(() => {
    if (isAdmin) return true;
    const crew = selectedJob?.assignedCrew || selectedJob?.assigned_crew || [];
    const me = (profile?.full_name || '').toLowerCase();
    return Array.isArray(crew) && crew.some((n) => (typeof n === 'string' ? n : n?.name || '').toLowerCase() === me);
  }, [isAdmin, selectedJob, profile]);

  const jobLines = useMemo(
    () => itemLines(selectedJob?.calcSnapshot),
    [selectedJob]
  );
  const canItems = !!selectedJobId && jobLines.length > 0;
  const isItems = payMode === 'items' && canItems;
  const isStaycation = payMode === 'staycation';

  // ── Staycation computation ────────────────────────────────────────────
  const stayResult = useMemo(() => computeStaycationSplit({
    revenue: numericAmount,
    companyTarget: parseFloat(stayCompanyTarget) || 0,
    hrsPerSegment: stayHrsPerSegment,
    rows: stayRows,
  }), [numericAmount, stayCompanyTarget, stayHrsPerSegment, stayRows]);

  // Logged hours per person on the selected job (informational chip)
  const stayLoggedHours = useMemo(() => {
    const map = {};
    for (const e of jobTimeEntries) {
      const name = (e.person || '').toLowerCase();
      if (!name) continue;
      map[name] = round2((map[name] || 0) + (parseFloat(e.hours) || 0));
    }
    return map;
  }, [jobTimeEntries]);

  const { pieces, mismatches: splitMismatches } = useMemo(
    () => buildItemPieces(jobLines, itemSplits),
    [jobLines, itemSplits]
  );

  // Full items computation: allocation + expenses + company/summary rollups.
  // Kept in ONE memo because the expense pass mutates the allocation result.
  const itemsView = useMemo(() => {
    if (!isItems) return null;
    const r = computeItemsAllocation({
      pieces,
      revenue: numericAmount,
      splits,
      assignments: itemAssign,
      timeEntries: jobTimeEntries,
    });
    const origRes = calculateOriginatorEarnings(r.origPool, originators);
    // Originator expense deduction reuses the legacy engine (orig bucket only)
    const emptyDept = { lead: null, hourly: [], budget: 0, hourlyTotal: 0, overrun: 0, leadEarned: 0 };
    const { origResult: adjOrig } = applyExpenseDeductions(
      origRes, emptyDept, emptyDept, { orig: expensesByDept.orig, prod: 0, post: 0 }
    );
    const absorbed = applyItemsExpenseDeductions(r, expensesByDept);
    const totalExpenses = round2(expensesByDept.orig + expensesByDept.prod + expensesByDept.post);
    const origAbsorbed = round2(Math.min(expensesByDept.orig,
      (origRes.originators || []).reduce((s, o) => s + o.earned, 0)));
    const expenseMeta = totalExpenses > 0 ? {
      totalExpenses,
      byDept: { ...expensesByDept },
      absorbed: { orig: origAbsorbed, prod: absorbed.prod, post: absorbed.post },
      unabsorbed: round2(totalExpenses - origAbsorbed - absorbed.prod - absorbed.post),
    } : null;
    const companyTotal = round2(
      r.companyBase + (adjOrig.remainder || 0) + r.unassignedToCompany - r.totalOverrun
    );
    return { r, origRes: adjOrig, expenseMeta, companyTotal };
  }, [isItems, pieces, numericAmount, splits, itemAssign, jobTimeEntries, originators, expensesByDept]);

  // ── Compute amounts & redistribution (legacy job-level mode) ───────────
  const { amounts: redistAmounts, redistribution } = useMemo(() => {
    const raw = {
      company: round2(numericAmount * splits.company / 100),
      orig: round2(numericAmount * splits.orig / 100),
      prod: round2(numericAmount * splits.prod / 100),
      post: round2(numericAmount * splits.post / 100),
    };
    // Items mode: no department-level redistribution — per-piece folding
    // happens inside computeItemsAllocation
    if (isItems) return { amounts: raw, redistribution: { prodToPost: 0, postToProd: 0, toCompany: 0 } };
    const hasProdCrew = !!(prodLead || prodHourly.length > 0);
    const hasPostCrew = !!(postLead || postHourly.length > 0);
    return redistributeEmpty(raw, hasProdCrew, hasPostCrew);
  }, [numericAmount, splits, isItems, prodLead, prodHourly.length, postLead, postHourly.length]);

  // ── Originator earnings ───────────────────────────────────────────────
  const origResult = useMemo(() => {
    return calculateOriginatorEarnings(redistAmounts.orig, originators);
  }, [redistAmounts.orig, originators]);

  // ── Department earnings ───────────────────────────────────────────────
  const prodResult = useMemo(() => {
    return calculateDeptEarnings(redistAmounts.prod, prodLead, prodHourly, jobTimeEntries);
  }, [redistAmounts.prod, prodLead, prodHourly, jobTimeEntries]);

  const postResult = useMemo(() => {
    return calculateDeptEarnings(redistAmounts.post, postLead, postHourly, jobTimeEntries);
  }, [redistAmounts.post, postLead, postHourly, jobTimeEntries]);

  // ── Expense deductions — reduce lead/originator earnings ──────────────
  const { origResult: adjOrigResult, prodResult: adjProdResult, postResult: adjPostResult, expenseMeta } = useMemo(() => {
    return applyExpenseDeductions(origResult, prodResult, postResult, expensesByDept);
  }, [origResult, prodResult, postResult, expensesByDept]);

  // ── Company total ─────────────────────────────────────────────────────
  const companyTotal = useMemo(() => {
    if (isStaycation) return stayResult.netProfit;
    if (isItems) return itemsView ? itemsView.companyTotal : 0;
    const totalOverrun = (adjProdResult.overrun || 0) + (adjPostResult.overrun || 0);
    const companyFromProd = !prodLead ? (adjProdResult.leadEarned || 0) : 0;
    const companyFromPost = !postLead ? (adjPostResult.leadEarned || 0) : 0;
    return round2(
      redistAmounts.company
      + (adjOrigResult.remainder || 0)
      + companyFromProd
      + companyFromPost
      - totalOverrun
    );
  }, [isStaycation, stayResult.netProfit, isItems, itemsView, redistAmounts.company, adjOrigResult.remainder, prodLead, postLead, adjProdResult, adjPostResult]);

  // ── Per-person aggregated earnings for summary ────────────────────────
  const perPersonEarnings = useMemo(() => {
    const map = {}; // name → { actual, estimated, roles[] }
    const add = (name, earned, role, isEstimated) => {
      if (!name) return;
      if (!map[name]) map[name] = { name, actual: 0, estimated: 0, roles: [] };
      if (isEstimated) map[name].estimated += earned;
      else map[name].actual += earned;
      if (!map[name].roles.includes(role)) map[name].roles.push(role);
    };

    if (isStaycation) {
      stayResult.crew.forEach(m => add(m.name, m.earned, 'Editor', true));
    } else if (isItems && itemsView) {
      itemsView.origRes.originators.forEach(o => add(o.name, o.earned, 'Orig', false));
      itemsView.r.itemAllocs.forEach(a => {
        [['shoot', 'Prod Lead', 'Prod'], ['edit', 'Post Lead', 'Post']].forEach(([side, leadRole, hourlyRole]) => {
          const s = a[side];
          const anyEst = (s.hourly || []).some(h => h.isEstimated);
          if (s.lead) add(s.lead.name, s.leadEarned, leadRole, anyEst);
          (s.hourly || []).forEach(m => add(m.name, m.earned, hourlyRole, m.isEstimated));
        });
      });
    } else {
      // Originators (never estimated)
      adjOrigResult.originators.forEach(o => add(o.name, o.earned, 'Orig', false));
      // Prod
      if (adjProdResult.lead) add(adjProdResult.lead.name, adjProdResult.leadEarned, 'Prod Lead', adjProdResult.hourly.some(h => h.isEstimated));
      adjProdResult.hourly.forEach(m => add(m.name, m.earned, 'Prod', m.isEstimated));
      // Post
      if (adjPostResult.lead) add(adjPostResult.lead.name, adjPostResult.leadEarned, 'Post Lead', adjPostResult.hourly.some(h => h.isEstimated));
      adjPostResult.hourly.forEach(m => add(m.name, m.earned, 'Post', m.isEstimated));
    }

    return Object.values(map)
      .map(p => ({ ...p, total: round2(p.actual + p.estimated), hasEstimated: p.estimated > 0 }))
      .sort((a, b) => b.total - a.total);
  }, [isStaycation, stayResult, isItems, itemsView, adjOrigResult, adjProdResult, adjPostResult]);

  // Expense/overrun figures for the summary card, whichever mode is active
  // (staycation has no expense/overrun concept — company keeps the remainder)
  const emptyExpenseMeta = { totalExpenses: 0, byDept: { orig: 0, prod: 0, post: 0 }, absorbed: {}, unabsorbed: 0 };
  const displayExpenseMeta = isStaycation
    ? emptyExpenseMeta
    : isItems
      ? (itemsView?.expenseMeta || emptyExpenseMeta)
      : expenseMeta;
  const displayOverrun = isStaycation
    ? 0
    : isItems
      ? (itemsView?.r.totalOverrun || 0)
      : round2((adjProdResult.overrun || 0) + (adjPostResult.overrun || 0));

  // ── Active jobs for selector ──────────────────────────────────────────
  const activeJobs = useMemo(() => {
    return [...allJobs]
      .filter(j => j.status !== 'void' && j.status !== 'archived')
      .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  }, [allJobs]);

  // ── Per-picker exclusion (same person CAN be in multiple departments) ─
  const getExcludedNames = useCallback((pickerKey) => {
    const names = new Set();
    if (pickerKey === 'orig') {
      // Can't be originator twice
      originators.forEach(o => names.add(o.name));
    } else if (pickerKey === 'prodLead') {
      // Can't be prod lead AND prod hourly
      prodHourly.forEach(m => names.add(m.name));
      if (prodLead) names.add(prodLead.name);
    } else if (pickerKey === 'prodHourly') {
      // Can't be prod hourly twice, can't also be prod lead
      prodHourly.forEach(m => names.add(m.name));
      if (prodLead) names.add(prodLead.name);
    } else if (pickerKey === 'postLead') {
      postHourly.forEach(m => names.add(m.name));
      if (postLead) names.add(postLead.name);
    } else if (pickerKey === 'postHourly') {
      postHourly.forEach(m => names.add(m.name));
      if (postLead) names.add(postLead.name);
    } else if (pickerKey === 'stayCrew') {
      stayRows.forEach(m => names.add(m.name));
    }
    return names;
  }, [originators, prodLead, prodHourly, postLead, postHourly, stayRows]);

  // Build lookup of name → roles assigned elsewhere (for picker badges)
  const assignedRoles = useMemo(() => {
    const map = {};
    originators.forEach(o => { (map[o.name] ??= []).push('Orig'); });
    if (prodLead) (map[prodLead.name] ??= []).push('Prod Lead');
    prodHourly.forEach(m => { (map[m.name] ??= []).push('Prod'); });
    if (postLead) (map[postLead.name] ??= []).push('Post Lead');
    postHourly.forEach(m => { (map[m.name] ??= []).push('Post'); });
    return map;
  }, [originators, prodLead, prodHourly, postLead, postHourly]);

  // ── Picker helpers ────────────────────────────────────────────────────
  const togglePicker = (key) => {
    setActivePicker(prev => prev === key ? null : key);
    setPickerSearch('');
  };

  const filteredRoster = useMemo(() => {
    const excluded = getExcludedNames(activePicker);
    return roster.filter(rm => {
      const name = parseRosterName(rm.name);
      if (excluded.has(name)) return false;
      if (pickerSearch && !name.toLowerCase().includes(pickerSearch.toLowerCase())) return false;
      return true;
    });
  }, [roster, activePicker, getExcludedNames, pickerSearch]);

  // ── Originator handlers ───────────────────────────────────────────────
  const handleAddOriginator = (rm) => {
    const name = parseRosterName(rm.name);
    setOriginators(prev => [...prev, {
      rosterId: rm.id,
      name,
      crewId: rm.crewId || null,
      pct: prev.length === 0 ? 100 : 0,
    }]);
    setActivePicker(null);
  };

  const handleRemoveOriginator = (idx) => {
    setOriginators(prev => prev.filter((_, i) => i !== idx));
  };

  const handleOrigPctChange = (idx, value) => {
    setOriginators(prev => prev.map((o, i) =>
      i === idx ? { ...o, pct: parseFloat(value) || 0 } : o
    ));
  };

  const handleAutoEqual = () => {
    setOriginators(prev => autoEqualOriginators(prev));
  };

  // ── Department handlers ───────────────────────────────────────────────
  const handleSetLead = (dept, rm) => {
    const name = parseRosterName(rm.name);
    const lead = { rosterId: rm.id, name, crewId: rm.crewId || null };
    if (dept === 'prod') setProdLead(lead);
    else setPostLead(lead);
    setActivePicker(null);
  };

  const handleRemoveLead = (dept) => {
    if (dept === 'prod') setProdLead(null);
    else setPostLead(null);
  };

  const handleAddHourly = (dept, rm) => {
    const name = parseRosterName(rm.name);
    const crewId = rm.crewId || null;
    const rateCategory = dept === 'prod' ? 'production' : 'post_production';
    const autoRate = getCrewRate(settings, crewId, rateCategory) || 0;

    const member = {
      rosterId: rm.id,
      name,
      crewId,
      hourlyRate: autoRate,
      estHours: 0,
    };

    if (dept === 'prod') setProdHourly(prev => [...prev, member]);
    else setPostHourly(prev => [...prev, member]);
    setActivePicker(null);
  };

  const handleRemoveHourly = (dept, idx) => {
    if (dept === 'prod') setProdHourly(prev => prev.filter((_, i) => i !== idx));
    else setPostHourly(prev => prev.filter((_, i) => i !== idx));
  };

  const handleHourlyChange = (dept, idx, field, value) => {
    const setter = dept === 'prod' ? setProdHourly : setPostHourly;
    setter(prev => prev.map((m, i) =>
      i === idx ? { ...m, [field]: parseFloat(value) || 0 } : m
    ));
  };

  // ── Items mode handlers ───────────────────────────────────────────────
  const patchAssign = (key, patch) => {
    setItemAssign(prev => ({
      ...prev,
      [key]: {
        soloLead: false, shootLead: null, editLead: null, shootHourly: [], editHourly: [],
        ...prev[key],
        ...patch,
      },
    }));
  };

  const rosterMember = (rm) => ({
    rosterId: rm.id, name: parseRosterName(rm.name), crewId: rm.crewId || null,
  });

  const handleSetItemSideLead = (key, side, rm) => {
    patchAssign(key, { [side === 'shoot' ? 'shootLead' : 'editLead']: rosterMember(rm), soloLead: false });
    setActivePicker(null);
  };

  const handleRemoveItemLead = (key, side) => {
    if (side === 'both') patchAssign(key, { shootLead: null, editLead: null, soloLead: false });
    else patchAssign(key, { [side === 'shoot' ? 'shootLead' : 'editLead']: null, soloLead: false });
  };

  const handleAddItemHourly = (key, side, rm) => {
    const crewId = rm.crewId || null;
    const rateCategory = side === 'shoot' ? 'production' : 'post_production';
    const member = {
      ...rosterMember(rm),
      hourlyRate: getCrewRate(settings, crewId, rateCategory) || 0,
      estHours: 0,
    };
    const field = side === 'shoot' ? 'shootHourly' : 'editHourly';
    const a = itemAssign[key] || {};
    patchAssign(key, { [field]: [...(a[field] || []), member] });
    setActivePicker(null);
  };

  const handleRemoveItemHourly = (key, side, idx) => {
    const field = side === 'shoot' ? 'shootHourly' : 'editHourly';
    const a = itemAssign[key] || {};
    patchAssign(key, { [field]: (a[field] || []).filter((_, i) => i !== idx) });
  };

  const handleItemHourlyChange = (key, side, idx, field, value) => {
    const listField = side === 'shoot' ? 'shootHourly' : 'editHourly';
    const a = itemAssign[key] || {};
    patchAssign(key, {
      [listField]: (a[listField] || []).map((m, i) =>
        i === idx ? { ...m, [field]: parseFloat(value) || 0 } : m
      ),
    });
  };

  // ── Staycation mode handlers ──────────────────────────────────────────
  const handleAddStayCrew = (rm) => {
    const name = parseRosterName(rm.name);
    const crewId = rm.crewId || null;
    setStayRows(prev => [...prev, {
      rosterId: rm.id,
      name,
      crewId,
      rate: getCrewRate(settings, crewId, 'post_production') || 0,
      segments: 0,
      extraHours: 0,
    }]);
    setActivePicker(null);
  };

  const handleRemoveStayCrew = (idx) => {
    setStayRows(prev => prev.filter((_, i) => i !== idx));
  };

  const handleStayRowChange = (idx, field, value) => {
    setStayRows(prev => prev.map((m, i) => {
      if (i !== idx) return m;
      const num = field === 'segments'
        ? Math.max(0, Math.round(parseFloat(value) || 0))
        : Math.max(0, parseFloat(value) || 0);
      return { ...m, [field]: num };
    }));
  };

  // Company target: $ is canonical; the % input converts through revenue
  const handleStayTargetPct = (value) => {
    const pct = parseFloat(value) || 0;
    setStayCompanyTarget(numericAmount > 0 ? String(round2(numericAmount * pct / 100)) : '');
  };

  const handleEnterStaycation = () => {
    const hasAllocs = Object.values(itemAssign).some(a => a.shootLead || a.editLead || a.shootHourly?.length || a.editHourly?.length);
    const hasLegacyCrew = !!(prodLead || postLead || prodHourly.length || postHourly.length || originators.length);
    const willDiscard = isItems ? hasAllocs : hasLegacyCrew;
    if (willDiscard && !window.confirm('Switch to Staycation split? The current crew assignments will be discarded on the next save.')) {
      return;
    }
    setPreStayMode(isItems ? 'items' : 'legacy');
    setPayMode('staycation');
  };

  const handleExitStaycation = () => {
    if (stayRows.length > 0 && !window.confirm('Switch back to standard split? The Staycation plan is discarded on the next save.')) {
      return;
    }
    // Return to the mode we came from (items only if the job still supports it)
    setPayMode(preStayMode === 'items' && canItems ? 'items' : 'legacy');
  };

  // Legacy → items: dept leads become that side's lead on every piece; dept
  // hourly attach to the largest piece of their side (engine attributes their
  // actual hours there)
  const handleUpgradeToItems = () => {
    // Back to per-item before a save: keep its assignments (leaving per-item
    // only discards them on save), unless the job-level crew changed since —
    // then offer to copy that instead
    const hasAllocs = Object.values(itemAssign).some(a => a.shootLead || a.editLead || a.shootHourly?.length || a.editHourly?.length);
    if (hasAllocs) {
      const changed = jobLevelAtLeaveRef.current != null
        && jobLevelAtLeaveRef.current !== JSON.stringify({ prodLead, postLead, prodHourly, postHourly });
      if (!changed || !window.confirm('Copy the job-level leads onto every item? Cancel keeps the per-item assignments you had.')) {
        setPayMode('items');
        return;
      }
    }
    const assign = {};
    const largest = pieces.reduce((best, p) => (p.amount > (best?.amount || 0) ? p : best), null);
    pieces.forEach(p => {
      const solo = !!(prodLead && postLead && prodLead.name === postLead.name);
      assign[p.key] = {
        soloLead: solo,
        shootLead: prodLead ? { ...prodLead } : null,
        editLead: postLead ? { ...postLead } : null,
        shootHourly: largest && p.key === largest.key ? prodHourly.map(m => ({ ...m })) : [],
        editHourly: largest && p.key === largest.key ? postHourly.map(m => ({ ...m })) : [],
      };
    });
    setItemAssign(assign);
    setPayMode('items');
  };

  const handleSwitchToLegacy = () => {
    const hasAllocs = Object.values(itemAssign).some(a => a.shootLead || a.editLead || a.shootHourly?.length || a.editHourly?.length);
    if (hasAllocs && !window.confirm('Switch to job-level mode? Per-item lead assignments will be discarded on the next save.')) {
      return;
    }
    jobLevelAtLeaveRef.current = JSON.stringify({ prodLead, postLead, prodHourly, postHourly });
    setPayMode('legacy');
  };

  // ── Split dialog (sub-divide one line into pieces) ────────────────────
  const handleSaveSplit = (sid, rows) => {
    const clean = rows
      .map(r => ({ id: r.id, desc: (r.desc || '').trim(), amount: round2(parseFloat(r.amount) || 0) }))
      .filter(r => r.desc || r.amount > 0);
    setItemSplits(prev => {
      const next = { ...prev };
      if (clean.length < 2) delete next[sid];
      else next[sid] = clean;
      return next;
    });
    setSplitDialogSid(null);
  };

  const handleRemoveSplit = (sid) => {
    setItemSplits(prev => {
      const next = { ...prev };
      delete next[sid];
      return next;
    });
    // Drop assignments for that line's sub-pieces
    setItemAssign(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (!k.startsWith(`${sid}:`)) next[k] = v;
      });
      return next;
    });
    setSplitDialogSid(null);
  };

  // ── AutoScale toggle ─────────────────────────────────────────────────
  const handleToggleAutoScale = () => {
    if (autoScale) {
      // Switching to manual — pre-fill with current formula values
      setManualSplits(calculateSplits(numericAmount, settingsData, true));
    }
    setAutoScale(!autoScale);
  };

  // ── Create new job from calculator ────────────────────────────────────
  const openNewJobDialog = () => {
    setNewJobTitle(numericAmount > 0 ? `Job — ${fmtC(numericAmount)}` : '');
    setNewJobContact(null);
    setClientSearch('');
    setShowContactDropdown(false);
    setShowNewJobDialog(true);
  };

  const handleContactSelect = useCallback((contact) => {
    setNewJobContact(contact);
    setClientSearch(contact.name);
    setShowContactDropdown(false);
  }, []);

  const handleConfirmNewJob = async () => {
    const id = uid();
    const now = new Date().toISOString();
    const title = newJobTitle.trim() || (numericAmount > 0 ? `Job — ${fmtC(numericAmount)}` : '(New Job)');
    const jobData = {
      id,
      title,
      revenue: numericAmount || 0,
      status: 'intake',
      createdAt: now,
      updatedAt: now,
    };
    if (newJobContact) {
      jobData.clientName = newJobContact.name;
      jobData.contactName = newJobContact.name;
      jobData.contactEmail = newJobContact.email || '';
      jobData.contactPhone = newJobContact.phone || '';
      jobData.contactId = newJobContact.id;
    } else if (clientSearch.trim()) {
      jobData.clientName = clientSearch.trim();
      jobData.contactName = clientSearch.trim();
    }
    try {
      await createJob.mutateAsync(jobData);
      setSelectedJobId(id);
      setShowNewJobDialog(false);
      toast('Job created — linked to calculator', 'success');
    } catch (err) {
      toast(`Failed to create job: ${err.message}`, 'error');
    }
  };

  // ── Save snapshot to job ──────────────────────────────────────────────
  const handleSaveToJob = async () => {
    if (!selectedJobId) {
      toast('Select a job first', 'error');
      return;
    }
    if (isItems && splitMismatches.length > 0) {
      toast(`Fix item splits first: ${splitMismatches.map(m => m.desc).join(', ')}`, 'error');
      return;
    }

    const existingJob = allJobs.find(j => j.id === selectedJobId);
    const snapshot = isStaycation
      ? buildStaycationSnapshot({
          revenue: numericAmount,
          result: stayResult,
          hrsPerSegment: stayHrsPerSegment,
          existingSnapshot: existingJob?.calcSnapshot,
        })
      : isItems && itemsView
      ? buildItemsSnapshot({
          revenue: numericAmount,
          autoScale,
          splits,
          origResult: itemsView.origRes,
          itemsResult: itemsView.r,
          itemSplits,
          expenseMeta: itemsView.expenseMeta,
          existingSnapshot: existingJob?.calcSnapshot,
        })
      : buildSnapshot({
          revenue: numericAmount,
          autoScale,
          splits,
          amounts: redistAmounts,
          redistribution,
          origResult: adjOrigResult,
          prodResult: adjProdResult,
          postResult: adjPostResult,
          expenseMeta: expenseMeta.totalExpenses > 0 ? expenseMeta : null,
          existingSnapshot: existingJob?.calcSnapshot,
        });

    try {
      const res = await updateJob.mutateAsync({
        id: selectedJobId,
        calcSnapshot: snapshot,
        // Optimistic concurrency: reject the save if another writer touched
        // the job since we last saw it (crew state here stays intact on 409)
        expectedUpdatedAt: jobUpdatedAt || existingJob?.updatedAt || undefined,
      });
      if (res?.updatedAt) setJobUpdatedAt(res.updatedAt);
      toast('Pay split saved to job', 'success');
      navigateToJob(navigate, selectedJobId);
    } catch (err) {
      if (err?.status === 409) {
        toast('Job changed elsewhere — refreshed it. Your assignments are kept; review and Save again.', 'error');
        try {
          const fresh = await api.get(`/jobs/${selectedJobId}`);
          setJobUpdatedAt(fresh?.updated_at || null);
        } catch { /* next websocket refresh will supply it */ }
      } else {
        toast(`Failed to save: ${err.message}`, 'error');
      }
    }
  };

  // ── Crew picker sub-component ─────────────────────────────────────────
  const renderPicker = (pickerKey, onSelect) => {
    if (activePicker !== pickerKey) return null;
    return (
      <div className="mb-3 p-3 bg-surface border border-border rounded">
        <input
          type="text"
          value={pickerSearch}
          onChange={e => setPickerSearch(e.target.value)}
          placeholder="Search roster..."
          className="w-full px-2 py-1.5 bg-surface-2 border border-border rounded text-xs text-text-primary mb-2 focus:outline-none focus:border-accent"
        />
        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
          {filteredRoster.length === 0 ? (
            <span className="text-2xs text-text-dim">No available crew members</span>
          ) : (
            filteredRoster.map(rm => {
              const name = parseRosterName(rm.name);
              const otherRoles = assignedRoles[name];
              return (
                <button
                  key={rm.id}
                  onClick={() => onSelect(rm)}
                  className={`px-2.5 py-1 rounded text-2xs border bg-surface border-border text-text-primary hover:border-accent/50 hover:bg-accent/10 transition-colors flex items-center gap-1.5 ${otherRoles ? 'ring-1 ring-accent/30' : ''}`}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getUserColor(rm) }} />
                  {name} {rm.isCore && '★'}{otherRoles ? ` · ${otherRoles.join(', ')}` : ''}
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // ── Department section renderer (Production / Post) ───────────────────
  const renderDeptSection = (dept, label, color, textColor, lead, hourly, result) => {
    const budget = dept === 'prod' ? redistAmounts.prod : redistAmounts.post;
    const splitPct = dept === 'prod' ? splits.prod : splits.post;

    return (
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${color}`} />
            <h3 className="text-sm font-heading text-text-primary">{label}</h3>
            <span className="text-xs text-text-dim">({splitPct}%)</span>
          </div>
          {numericAmount > 0 && (
            <span className={`text-sm font-heading ${textColor}`}>{fmtC(budget)}</span>
          )}
        </div>

        {/* ── Lead ── */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-2xs font-semibold text-text-dim uppercase tracking-wide">
              Budget Lead
            </span>
            {!lead && (
              <button
                onClick={() => togglePicker(`${dept}Lead`)}
                className="px-2 py-1 bg-accent/10 border border-accent/30 rounded text-2xs text-accent hover:bg-accent/20 transition-colors"
              >
                + Set Lead
              </button>
            )}
          </div>

          {renderPicker(`${dept}Lead`, rm => handleSetLead(dept, rm))}

          {lead ? (
            <div className="flex items-center gap-2 p-2 bg-surface rounded border border-border">
              <UserAvatar name={lead.name} color={getUserColor(roster.find(r => parseRosterName(r.name) === lead.name) || { name: lead.name })} size="sm" />
              <span className="text-xs font-semibold text-text-primary flex-1">{lead.name}</span>
              {numericAmount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className={`text-xs font-semibold ${result.hourly.some(h => h.isEstimated) ? 'text-yellow-500' : textColor}`}>
                    {result.hourly.some(h => h.isEstimated) && '~'}{fmtC(result.leadEarned)}
                  </span>
                  {result.hourly.some(h => h.isEstimated) && (
                    <span className="text-2xs text-yellow-500 italic">est</span>
                  )}
                  {result.expenseDeduction > 0 && (
                    <span className="text-2xs text-orange-400" title={`Expense deduction: -${fmtC(result.expenseDeduction)}`}>
                      (-{fmtC(result.expenseDeduction)})
                    </span>
                  )}
                </span>
              )}
              <button
                onClick={() => handleRemoveLead(dept)}
                className="text-red-accent text-2xs hover:bg-red-accent/10 rounded flex-shrink-0 transition-colors inline-flex items-center justify-center min-w-[40px] min-h-[40px] md:min-w-0 md:min-h-0 md:px-1.5 md:py-0.5"
              >
                ✕
              </button>
            </div>
          ) : (
            <p className="text-2xs text-text-dim py-1">No lead — remainder goes to Company</p>
          )}
        </div>

        {/* ── Hourly Crew ── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-2xs font-semibold text-text-dim uppercase tracking-wide">
              Hourly Crew
            </span>
            {!(dept === 'post' && hourly.length === 0 && isHidden('calc.postAddCrew')) && (
            <button
              data-focus={dept === 'post' ? 'calc.postAddCrew' : undefined}
              onClick={() => togglePicker(`${dept}Hourly`)}
              className="px-2 py-1 bg-accent/10 border border-accent/30 rounded text-2xs text-accent hover:bg-accent/20 transition-colors"
            >
              + Add Crew
            </button>
            )}
          </div>

          {renderPicker(`${dept}Hourly`, rm => handleAddHourly(dept, rm))}

          {hourly.length === 0 ? (
            <p className="text-2xs text-text-dim py-1">No hourly crew assigned</p>
          ) : (
            <div className="space-y-2">
              {result.hourly.map((m, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-2 p-2 bg-surface rounded border border-border">
                  <UserAvatar name={m.name} color={getUserColor(roster.find(r => parseRosterName(r.name) === m.name) || { name: m.name })} size="sm" />
                  <span className="text-xs font-semibold text-text-primary min-w-0 truncate flex-shrink-0 w-24">
                    {m.name}
                  </span>

                  {/* Rate */}
                  <div className="flex items-center gap-1">
                    <span className="text-2xs text-text-dim">$</span>
                    <input
                      type="number"
                      value={hourly[idx]?.hourlyRate || 0}
                      onChange={e => handleHourlyChange(dept, idx, 'hourlyRate', e.target.value)}
                      min="0"
                      step="5"
                      className="w-14 px-1.5 py-1 bg-surface-2 border border-border rounded text-2xs text-text-primary text-center focus:outline-none focus:border-accent"
                    />
                    <span className="text-2xs text-text-dim">/hr</span>
                  </div>

                  {/* Est Hours */}
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={hourly[idx]?.estHours || 0}
                      onChange={e => handleHourlyChange(dept, idx, 'estHours', e.target.value)}
                      min="0"
                      step="0.5"
                      className="w-12 px-1.5 py-1 bg-surface-2 border border-border rounded text-2xs text-text-primary text-center focus:outline-none focus:border-accent"
                      placeholder="est"
                    />
                    <span className="text-2xs text-text-dim">est h</span>
                  </div>

                  {m.hours > 0 && (
                    <span className="text-2xs text-text-dim">({m.hours}h logged)</span>
                  )}
                  {m.isEstimated && (
                    <span className="text-2xs text-yellow-500 italic">est</span>
                  )}

                  {/* Earned */}
                  {numericAmount > 0 && (
                    <span className={`text-xs font-semibold ml-auto ${m.isEstimated ? 'text-yellow-500' : textColor}`}>
                      {m.isEstimated && '~'}{fmtC(m.earned)}{m.isEstimated && ' est'}
                    </span>
                  )}

                  <button
                    onClick={() => handleRemoveHourly(dept, idx)}
                    className="text-red-accent text-2xs hover:bg-red-accent/10 rounded flex-shrink-0 transition-colors inline-flex items-center justify-center min-w-[40px] min-h-[40px] md:min-w-0 md:min-h-0 md:px-1.5 md:py-0.5"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Overrun warning */}
        {result.overrun > 0 && (
          <div className="mt-3 p-2 bg-red-accent/10 border border-red-accent/30 rounded">
            <p className="text-2xs text-red-accent font-semibold">
              ⚠ Budget overrun: {fmtC(result.overrun)} — absorbed by Company
            </p>
          </div>
        )}

        {/* Budget utilization */}
        {numericAmount > 0 && hourly.length > 0 && (
          <div className="mt-2 text-2xs text-text-dim">
            Hourly total: {fmtC(result.hourlyTotal)} / {fmtC(budget)} budget
          </div>
        )}
      </div>
    );
  };

  // ── Items section (per-line-item leads) ────────────────────────────────
  const renderItemHourlyRows = (piece, alloc, side, label, textColor) => {
    const a = itemAssign[piece.key] || {};
    const defs = side === 'shoot' ? (a.shootHourly || []) : (a.editHourly || []);
    const computedHourly = alloc?.[side]?.hourly || [];
    if (defs.length === 0) return null;
    return (
      <div className="mt-1.5 space-y-1">
        {defs.map((m, idx) => {
          const c = computedHourly.find(h => (h.name || '').toLowerCase() === (m.name || '').toLowerCase()) || {};
          return (
            <div key={`${m.name}-${idx}`} className="flex flex-wrap items-center gap-2 pl-2 border-l-2 border-border">
              <span className="text-2xs text-text-dim w-10 flex-shrink-0">{label}</span>
              <span className="text-xs text-text-primary truncate max-w-[110px]">{m.name}</span>
              <span className="flex items-center gap-0.5 text-2xs text-text-dim">
                $<input
                  type="number"
                  value={m.hourlyRate || 0}
                  onChange={e => handleItemHourlyChange(piece.key, side, idx, 'hourlyRate', e.target.value)}
                  min="0" step="5"
                  className="w-14 px-1 py-0.5 bg-surface-2 border border-border rounded text-2xs text-text-primary text-center focus:outline-none focus:border-accent"
                />/hr
              </span>
              <span className="flex items-center gap-0.5 text-2xs text-text-dim">
                <input
                  type="number"
                  value={m.estHours || 0}
                  onChange={e => handleItemHourlyChange(piece.key, side, idx, 'estHours', e.target.value)}
                  min="0" step="0.5"
                  className="w-12 px-1 py-0.5 bg-surface-2 border border-border rounded text-2xs text-text-primary text-center focus:outline-none focus:border-accent"
                /> est h
              </span>
              {c.hours > 0 && !c.isEstimated && <span className="text-2xs text-text-dim">({c.hours}h)</span>}
              {numericAmount > 0 && (
                <span className={cn('text-xs font-semibold ml-auto', c.isEstimated ? 'text-yellow-500' : textColor)}>
                  {c.isEstimated && '~'}{fmtC(c.earned || 0)}
                </span>
              )}
              <button
                onClick={() => handleRemoveItemHourly(piece.key, side, idx)}
                className="text-red-accent text-2xs hover:bg-red-accent/10 rounded transition-colors inline-flex items-center justify-center min-w-[40px] min-h-[40px] md:min-w-0 md:min-h-0 md:px-1 md:py-0.5"
              >✕</button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLeadChip = (lead, earned, isEst, onRemove) => (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-surface-2 rounded border border-border">
      <UserAvatar name={lead.name} color={getUserColor(roster.find(r => parseRosterName(r.name) === lead.name) || { name: lead.name })} size="sm" />
      <span className="text-xs font-semibold text-text-primary">{lead.name}</span>
      {numericAmount > 0 && (
        <span className={cn('text-xs font-semibold', isEst ? 'text-yellow-500' : 'text-green')}>
          {isEst && '~'}{fmtC(earned)}
        </span>
      )}
      <button onClick={onRemove} className="text-red-accent text-2xs hover:bg-red-accent/10 rounded transition-colors inline-flex items-center justify-center min-w-[40px] min-h-[40px] md:min-w-0 md:min-h-0 md:px-1">✕</button>
    </span>
  );

  // Two-table structure: Production lists every item's shoot share, Post
  // Production lists every item's edit share. Assign a person per item row;
  // same person on both sides = they own the whole item.
  const renderItemsDeptCard = (dept) => {
    const side = dept === 'prod' ? 'shoot' : 'edit';
    const otherSide = side === 'shoot' ? 'edit' : 'shoot';
    const otherLabel = dept === 'prod' ? 'Post' : 'Prod';
    const label = dept === 'prod' ? 'Production' : 'Post Production';
    const color = dept === 'prod' ? 'bg-green' : 'bg-purple-500';
    const textColor = dept === 'prod' ? 'text-green' : 'text-purple-400';
    const r = itemsView?.r || null;
    const allocByKey = new Map((r?.itemAllocs || []).map(al => [al.key, al]));
    const deptTotal = r ? r.amounts[dept] : 0;

    return (
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${color}`} />
            <h3 className="text-sm font-heading text-text-primary">{label}</h3>
            <span className="text-xs text-text-dim">({splits[dept]}%)</span>
          </div>
          {numericAmount > 0 && r && (
            <span className={`text-sm font-heading ${textColor}`}>{fmtC(deptTotal)}</span>
          )}
        </div>

        <div className="space-y-2">
          {pieces.map(piece => {
            const alloc = allocByKey.get(piece.key);
            const sideAlloc = alloc?.[side];
            const a = itemAssign[piece.key] || {};
            const lead = side === 'shoot' ? a.shootLead : a.editLead;
            const otherLead = side === 'shoot' ? a.editLead : a.shootLead;
            const isSub = piece.subId != null;
            const subDesc = isSub ? piece.desc.slice(piece.lineDesc.length).replace(/^ — /, '') : null;
            const anyEst = (sideAlloc?.hourly || []).some(h => h.isEstimated);
            // Share 0 = this side folded into the other one (no crew here)
            const folded = sideAlloc && sideAlloc.share === 0 && (alloc?.[otherSide]?.share || 0) > 0;

            return (
              <div key={piece.key} className={cn('p-2 bg-surface rounded border border-border', isSub && 'ml-3')}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-text-primary min-w-0 flex-1 truncate" title={piece.desc}>
                    {isSub ? `↳ ${subDesc}` : piece.desc}
                  </span>
                  {numericAmount > 0 && sideAlloc && (
                    folded
                      ? <span className="text-2xs text-text-dim italic">→ {otherLabel}</span>
                      : <span className="text-2xs text-text-dim">{fmtC(sideAlloc.share)}</span>
                  )}
                  {!isSub && dept === 'prod' && (!isHidden('calc.itemSplit') || itemSplits[piece.sid]) && (
                    <button
                      data-focus="calc.itemSplit"
                      onClick={() => setSplitDialogSid(piece.sid)}
                      className="px-1.5 py-0.5 text-2xs text-text-dim border border-border border-dashed rounded hover:border-accent/50 hover:text-accent transition-colors"
                      title="Divide this item into pieces with different leads"
                    >
                      Split
                    </button>
                  )}
                  {isSub && dept === 'prod' && piece === pieces.find(p => p.sid === piece.sid) && null}
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {lead ? (
                    renderLeadChip(lead, sideAlloc?.leadEarned || 0, anyEst, () => handleRemoveItemLead(piece.key, side))
                  ) : (
                    <button
                      onClick={() => togglePicker(`item:${piece.key}:${side}`)}
                      className="px-2 py-1 bg-accent/10 border border-accent/30 rounded text-2xs text-accent hover:bg-accent/20 transition-colors"
                    >
                      + Lead
                    </button>
                  )}
                  {/* Quick action: same person owns the other side too */}
                  {lead && !otherLead && (
                    <button
                      onClick={() => patchAssign(piece.key, {
                        [otherSide === 'shoot' ? 'shootLead' : 'editLead']: { ...lead },
                        soloLead: true,
                      })}
                      className="px-1.5 py-0.5 text-2xs text-text-dim border border-border border-dashed rounded hover:border-accent/50 hover:text-accent transition-colors"
                    >
                      also {otherLabel} →
                    </button>
                  )}
                  {!lead && !folded && <span className="text-2xs text-text-dim italic">→ Company</span>}
                  <button
                    onClick={() => togglePicker(`item:${piece.key}:${side}Hourly`)}
                    className="ml-auto px-1.5 py-0.5 text-2xs text-text-dim border border-border border-dashed rounded hover:border-accent/50 hover:text-accent transition-colors"
                  >
                    + Crew
                  </button>
                </div>
                {renderPicker(`item:${piece.key}:${side}`, rm => handleSetItemSideLead(piece.key, side, rm))}
                {renderPicker(`item:${piece.key}:${side}Hourly`, rm => handleAddItemHourly(piece.key, side, rm))}
                {renderItemHourlyRows(piece, alloc, side, dept === 'prod' ? 'Shoot' : 'Edit', textColor)}

                {sideAlloc?.overrun > 0 && (
                  <p className="mt-1.5 text-2xs text-red-accent font-semibold">
                    ⚠ Over budget by {fmtC(sideAlloc.overrun)} — absorbed by Company
                  </p>
                )}
              </div>
            );
          })}
          {pieces.length === 0 && (
            <p className="text-2xs text-text-dim py-2">No line items on this job.</p>
          )}
        </div>
      </div>
    );
  };

  const renderItemsBanners = () => {
    const r = itemsView?.r || null;
    if (!r && splitMismatches.length === 0 && orphanedAllocs.length === 0) return null;
    return (
      <>
        {r && Math.abs(r.scale - 1) > 0.001 && (
          <p className="text-2xs text-blue-400">
            Item amounts scaled ×{r.scale} to match job revenue (discount spread pro-rata)
          </p>
        )}
        {splitMismatches.length > 0 && (
          <div className="p-2 bg-red-accent/10 border border-red-accent/30 rounded">
            <p className="text-2xs text-red-accent font-semibold">
              ⚠ Split out of sync with line total: {splitMismatches.map(m => m.desc).join(', ')} — edit the split before saving
            </p>
          </div>
        )}
        {orphanedAllocs.length > 0 && (
          <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
            <p className="text-2xs text-yellow-500">
              Items changed since last save — reassign: {orphanedAllocs.join(', ')}
            </p>
          </div>
        )}
        {r && r.unassignedToCompany > 0 && (
          <p className="text-2xs text-text-dim">
            Unassigned item budgets: {fmtC(r.unassignedToCompany)} → Company
          </p>
        )}
      </>
    );
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────────
  return (
    <div data-track-area="calculator" className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        {!embedded && (
        <div>
          <h1 className="section-heading mb-0.5">Pay Calculator</h1>
          <p className="text-text-dim text-sm">
            Calculate revenue splits and assign crew earnings
          </p>
        </div>
        )}
        {selectedJobId && (
          <button
            onClick={handleSaveToJob}
            disabled={updateJob.isPending || numericAmount <= 0 || (isItems && splitMismatches.length > 0)}
            className="px-4 py-2 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover disabled:opacity-50 transition-colors"
          >
            {updateJob.isPending ? 'Saving...' : 'Save to Job'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── LEFT COLUMN: Config ────────────────────────────────────── */}
        <div className="lg:col-span-1 space-y-4">

          {/* Job Selector */}
          <div className="card p-4">
            <label className="block text-xs font-semibold text-text-dim uppercase tracking-wide mb-2">
              Job (Optional)
            </label>
            <select
              value={selectedJobId}
              onChange={e => setSelectedJobId(e.target.value)}
              className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors"
            >
              <option value="">Standalone (no job)</option>
              {activeJobs.map(j => (
                <option key={j.id} value={j.id}>
                  {j.title || j.clientName || '(Untitled)'} — {fmtC(j.revenue)}
                </option>
              ))}
            </select>
            <FocusHide k="calc.newJob">
            {!selectedJobId && (
              <button
                onClick={openNewJobDialog}
                disabled={createJob.isPending}
                className="mt-2 w-full py-2 bg-accent/10 border border-accent/30 border-dashed rounded text-xs text-accent font-semibold hover:bg-accent/20 disabled:opacity-50 transition-colors"
              >
                + New Job
              </button>
            )}
            </FocusHide>

            {/* Inline New Job Dialog */}
            {showNewJobDialog && (
              <div className="mt-3 p-3 bg-surface-alt border border-accent/30 rounded-lg space-y-3">
                {/* Contact search */}
                <div className="relative">
                  <label className="block text-2xs font-semibold text-text-dim uppercase tracking-wide mb-1">
                    Client / Vendor
                  </label>
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={e => {
                      setClientSearch(e.target.value);
                      setNewJobContact(null);
                      setShowContactDropdown(true);
                    }}
                    onFocus={() => setShowContactDropdown(true)}
                    onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
                    placeholder="Search contacts…"
                    className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
                  />
                  {showContactDropdown && filteredContacts.length > 0 && (
                    <ul className="absolute z-30 mt-1 w-full max-h-48 overflow-y-auto bg-surface border border-border rounded shadow-lg">
                      {filteredContacts.slice(0, 20).map(c => (
                        <li
                          key={c.id}
                          onClick={() => handleContactSelect(c)}
                          className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-accent/10 transition-colors"
                        >
                          <span className="text-text-primary truncate">
                            {c.name}{c.company ? ` — ${c.company}` : ''}
                          </span>
                          <span className={cn(
                            'ml-2 px-1.5 py-0.5 text-2xs rounded font-semibold shrink-0',
                            c.type === 'client' ? 'bg-green-500/15 text-green-400' : 'bg-blue-500/15 text-blue-400'
                          )}>
                            {c.type}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Job title */}
                <div>
                  <label className="block text-2xs font-semibold text-text-dim uppercase tracking-wide mb-1">
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={newJobTitle}
                    onChange={e => setNewJobTitle(e.target.value)}
                    placeholder="e.g. Aerial shoot — 123 Main St"
                    className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowNewJobDialog(false)}
                    className="px-3 py-1.5 text-xs text-text-dim border border-border rounded hover:bg-surface transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmNewJob}
                    disabled={createJob.isPending}
                    className="px-3 py-1.5 text-xs font-semibold text-bg bg-accent rounded hover:bg-accent-hover disabled:opacity-50 transition-colors"
                  >
                    {createJob.isPending ? 'Creating…' : 'Create Job'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Revenue Amount */}
          <div className="card p-4">
            <label className="block text-xs font-semibold text-text-dim uppercase tracking-wide mb-2">
              Revenue Amount
              {selectedJobId && (
                <span className="ml-2 text-2xs font-normal text-text-dim italic">from job — edit via requote</span>
              )}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim text-lg">$</span>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                disabled={!!selectedJobId}
                className={cn(
                  'w-full pl-8 pr-4 py-2.5 bg-surface border border-border rounded text-text-primary text-xl font-heading focus:outline-none focus:border-accent transition-colors',
                  selectedJobId && 'opacity-60 cursor-not-allowed'
                )}
              />
            </div>
            {!selectedJobId && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {QUICK_AMOUNTS.map(qa => (
                  <button
                    key={qa}
                    onClick={() => setAmount(String(qa))}
                    className={cn(
                      'px-2 py-1 text-2xs rounded border transition-colors',
                      numericAmount === qa
                        ? 'bg-accent text-bg border-accent font-semibold'
                        : 'bg-surface border-border text-text-dim hover:border-accent/50'
                    )}
                  >
                    {fmtC(qa)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Staycation setup (replaces split percentages in staycation mode) */}
          {isStaycation ? (
          <div className="card p-4">
            <h3 className="text-xs font-semibold text-text-dim uppercase tracking-wide mb-3">
              Staycation Split
            </h3>

            {/* Company target — linked $ and % inputs (company-visible only) */}
            {canSeeCompany && (
              <>
                <label className="block text-2xs font-semibold text-text-dim uppercase tracking-wide mb-1.5">
                  Company Target
                </label>
                <div className="flex items-center gap-2 mb-3">
                  <div className="relative flex-1">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-dim text-sm">$</span>
                    <input
                      type="number"
                      value={stayCompanyTarget}
                      onChange={e => setStayCompanyTarget(e.target.value)}
                      placeholder="0"
                      min="0"
                      step="100"
                      className="w-full pl-6 pr-2 py-2 bg-surface border border-border rounded text-text-primary text-sm font-heading focus:outline-none focus:border-accent transition-colors"
                    />
                  </div>
                  <span className="text-2xs text-text-dim">=</span>
                  <div className="relative w-24">
                    <input
                      type="number"
                      value={stayResult.targetPct}
                      onChange={e => handleStayTargetPct(e.target.value)}
                      min="0"
                      max="100"
                      step="1"
                      disabled={numericAmount <= 0}
                      className={cn(
                        'w-full pl-2 pr-6 py-2 bg-surface border border-border rounded text-text-primary text-sm text-right focus:outline-none focus:border-accent transition-colors',
                        numericAmount <= 0 && 'opacity-60 cursor-not-allowed'
                      )}
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-dim text-sm">%</span>
                  </div>
                </div>
              </>
            )}

            {/* Hours per segment */}
            <label className="block text-2xs font-semibold text-text-dim uppercase tracking-wide mb-1.5">
              Hours per Segment
            </label>
            <input
              type="number"
              value={stayHrsPerSegment}
              onChange={e => setStayHrsPerSegment(Math.max(0, parseFloat(e.target.value) || 0))}
              min="0"
              step="1"
              className="w-24 px-2 py-2 bg-surface border border-border rounded text-text-primary text-sm text-center focus:outline-none focus:border-accent transition-colors"
            />

            {/* Plan digest */}
            {numericAmount > 0 && (
              <div className="mt-3 pt-3 border-t border-border space-y-1">
                {canSeeCompany && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-muted">Available for labor</span>
                    <span className="font-semibold text-text-primary">{fmtC(stayResult.availableForLabor)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-muted">Projected labor</span>
                  <span className="font-semibold text-purple-400">{fmtC(stayResult.laborTotal)}</span>
                </div>
                {canSeeCompany && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-muted">Company nets</span>
                    <span className="font-semibold text-accent">{fmtC(stayResult.netProfit)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          ) : (
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-text-dim uppercase tracking-wide">
                Split Percentages
              </h3>
              <span className={cn(
                'text-xs font-semibold',
                Math.abs(totalPct - 100) < 0.01 ? 'text-green' : 'text-yellow-500'
              )}>
                {totalPct}%
              </span>
            </div>

            {/* AutoScale toggle */}
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={handleToggleAutoScale}
                className={cn(
                  'relative w-9 h-5 rounded-full transition-colors',
                  autoScale ? 'bg-accent' : 'bg-surface-3'
                )}
              >
                <span className={cn(
                  'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform',
                  autoScale ? 'left-[18px]' : 'left-0.5'
                )} />
              </button>
              <span className="text-xs text-text-muted">Auto-Scale</span>
              {autoScale && (
                <span className="text-2xs text-text-dim">(revenue formula)</span>
              )}
            </div>

            {/* Split bar */}
            {numericAmount > 0 && (
              <div className="h-2 rounded-full bg-surface-3 overflow-hidden flex mb-3">
                {canSeeCompany && splits.company > 0 && (
                  <div className="bg-accent h-full" style={{ width: `${splits.company}%` }} title={`Company: ${splits.company}%`} />
                )}
                {splits.orig > 0 && (
                  <div className="bg-blue-500 h-full" style={{ width: `${splits.orig}%` }} title={`Originator: ${splits.orig}%`} />
                )}
                {splits.prod > 0 && (
                  <div className="bg-green h-full" style={{ width: `${splits.prod}%` }} title={`Production: ${splits.prod}%`} />
                )}
                {splits.post > 0 && (
                  <div className="bg-purple-500 h-full" style={{ width: `${splits.post}%` }} title={`Post: ${splits.post}%`} />
                )}
              </div>
            )}

            {/* Split rows */}
            <div className="space-y-2">
              {[
                { key: 'company', label: 'Company', color: 'bg-accent', textColor: 'text-accent' },
                { key: 'orig', label: 'Originator', color: 'bg-blue-500', textColor: 'text-blue-400' },
                { key: 'prod', label: 'Production', color: 'bg-green', textColor: 'text-green' },
                { key: 'post', label: 'Post Prod', color: 'bg-purple-500', textColor: 'text-purple-400' },
              ].filter(role => canSeeCompany || role.key !== 'company').map(role => (
                <div key={role.key} className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${role.color} flex-shrink-0`} />
                  <span className="text-xs text-text-muted w-20 flex-shrink-0">{role.label}</span>
                  {autoScale ? (
                    <span className="w-16 text-xs text-text-primary text-center">
                      {splits[role.key]}%
                    </span>
                  ) : (
                    <input
                      type="number"
                      value={manualSplits[role.key]}
                      onChange={e => setManualSplits(prev => ({
                        ...prev,
                        [role.key]: parseFloat(e.target.value) || 0,
                      }))}
                      min="0"
                      max="100"
                      step="1"
                      className="w-16 px-2 py-1 bg-surface border border-border rounded text-text-primary text-xs text-center focus:outline-none focus:border-accent transition-colors"
                    />
                  )}
                  <span className="text-xs text-text-dim">%</span>
                  {numericAmount > 0 && (
                    <span className={`text-xs font-semibold ml-auto ${role.textColor}`}>
                      {fmtC(redistAmounts[role.key])}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Redistribution notes */}
            {redistribution.prodToPost > 0 && (
              <p className="text-2xs text-blue-400 mt-2">
                Prod → Post: +{fmtC(redistribution.prodToPost)}
              </p>
            )}
            {redistribution.postToProd > 0 && (
              <p className="text-2xs text-blue-400 mt-2">
                Post → Prod: +{fmtC(redistribution.postToProd)}
              </p>
            )}
            {redistribution.toCompany > 0 && (
              <p className="text-2xs text-blue-400 mt-2">
                Empty depts → Company: +{fmtC(redistribution.toCompany)}
              </p>
            )}

            {!autoScale && Math.abs(totalPct - 100) > 0.01 && totalPct > 0 && (
              <p className="text-2xs text-yellow-500 mt-2">
                Total should be 100% (currently {totalPct}%)
              </p>
            )}
          </div>
          )}
        </div>

        {/* ── RIGHT COLUMN: Crew Sections ────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">

          {/* ── Originator Section (not part of the staycation model) ── */}
          {!isStaycation && (
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500" />
                <h3 className="text-sm font-heading text-text-primary">Originators</h3>
                <span className="text-xs text-text-dim">({splits.orig}%)</span>
              </div>
              <div className="flex items-center gap-2">
                {numericAmount > 0 && originators.length > 0 && (
                  <span className="text-sm font-heading text-blue-400">
                    {fmtC(redistAmounts.orig)}
                  </span>
                )}
                {numericAmount > 0 && originators.length === 0 && (
                  <span className="text-xs text-text-dim italic">
                    {fmtC(redistAmounts.orig)} → Company
                  </span>
                )}
                {originators.length > 1 && (
                  <button
                    onClick={handleAutoEqual}
                    className="px-2 py-1 bg-blue-500/10 border border-blue-500/30 rounded text-2xs text-blue-400 hover:bg-blue-500/20 transition-colors"
                  >
                    Equal ÷
                  </button>
                )}
                <button
                  onClick={() => togglePicker('orig')}
                  className="px-2 py-1 bg-accent/10 border border-accent/30 rounded text-2xs text-accent hover:bg-accent/20 transition-colors"
                >
                  + Add
                </button>
              </div>
            </div>

            {renderPicker('orig', handleAddOriginator)}

            {originators.length === 0 ? (
              <p className="text-2xs text-text-dim py-2">
                No originators — entire pool goes to Company
              </p>
            ) : (
              <div className="space-y-2">
                {adjOrigResult.originators.map((o, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2 p-2 bg-surface rounded border border-border">
                    <UserAvatar name={o.name} color={getUserColor(roster.find(r => parseRosterName(r.name) === o.name) || { name: o.name })} size="sm" />
                    <span className="text-xs font-semibold text-text-primary min-w-0 truncate flex-shrink-0 w-28">
                      {o.name}
                    </span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={originators[idx]?.pct || 0}
                        onChange={e => handleOrigPctChange(idx, e.target.value)}
                        min="0"
                        max="100"
                        step="1"
                        className="w-14 px-1.5 py-1 bg-surface-2 border border-border rounded text-2xs text-text-primary text-center focus:outline-none focus:border-accent"
                      />
                      <span className="text-2xs text-text-dim">%</span>
                    </div>
                    {numericAmount > 0 && (
                      <div className="ml-auto flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-blue-400">
                          {fmtC(o.earned)}
                        </span>
                        {o.expenseDeduction > 0 && (
                          <span className="text-2xs text-orange-400" title={`Expense deduction: -${fmtC(o.expenseDeduction)}`}>
                            (-{fmtC(o.expenseDeduction)})
                          </span>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => handleRemoveOriginator(idx)}
                      className="text-red-accent text-2xs hover:bg-red-accent/10 rounded flex-shrink-0 transition-colors inline-flex items-center justify-center min-w-[40px] min-h-[40px] md:min-w-0 md:min-h-0 md:px-1.5 md:py-0.5"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {adjOrigResult.error && (
              <p className="text-2xs text-red-accent mt-2">⚠ {adjOrigResult.error}</p>
            )}
            {adjOrigResult.remainder > 0 && originators.length > 0 && numericAmount > 0 && (
              <p className="text-2xs text-text-dim mt-2">
                Remainder: {fmtC(adjOrigResult.remainder)} → Company
              </p>
            )}
          </div>
          )}

          {/* ── Mode toggle: per-item / staycation / job-level ───────── */}
          {(isStaycation || canItems || !isHidden('calc.staycationSplit')) && (
          <div className="flex flex-wrap items-center gap-2 -mb-1">
            {isStaycation ? (
              <button
                onClick={handleExitStaycation}
                className="text-2xs text-text-dim hover:text-accent transition-colors"
              >
                ← Switch back to standard split
              </button>
            ) : (
              <>
                {/* Never Focus-hideable: it is the only way into per-item on a
                    job saved job-level */}
                {canItems && (
                  <fieldset aria-label="Split by" className="inline-flex rounded border border-border overflow-hidden">
                    {[
                      { label: 'Job-level', on: !isItems, onClick: handleSwitchToLegacy, title: 'One Production lead and one Post lead for the whole job' },
                      { label: 'Per item', on: isItems, onClick: handleUpgradeToItems, title: 'Assign a lead to each quoted item' },
                    ].map(opt => (
                      <button
                        key={opt.label}
                        type="button"
                        aria-pressed={opt.on}
                        title={opt.title}
                        onClick={opt.on ? undefined : opt.onClick}
                        className={cn(
                          'px-2.5 py-1.5 text-xs transition-colors',
                          opt.on ? 'bg-accent/15 text-accent font-semibold cursor-default' : 'bg-surface text-text-dim hover:text-accent'
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </fieldset>
                )}
                <FocusHide k="calc.staycationSplit">
                <button
                  onClick={handleEnterStaycation}
                  className="px-2.5 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded text-xs text-purple-400 font-semibold hover:bg-purple-500/20 transition-colors"
                >
                  ✦ Staycation split — segments × hourly, company target
                </button>
                </FocusHide>
              </>
            )}
          </div>
          )}

          {isStaycation ? (
            /* ── Staycation crew — segments + extra hours per editor ── */
            <div className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-purple-500" />
                  <h3 className="text-sm font-heading text-text-primary">Staycation Crew</h3>
                  {stayResult.totalSegments > 0 && (
                    <span className="text-xs text-text-dim">
                      {stayResult.totalSegments} segments · {stayResult.totalHours}h planned
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {numericAmount > 0 && stayRows.length > 0 && (
                    <span className="text-sm font-heading text-purple-400">{fmtC(stayResult.laborTotal)}</span>
                  )}
                  <button
                    onClick={() => togglePicker('stayCrew')}
                    className="px-2 py-1 bg-accent/10 border border-accent/30 rounded text-2xs text-accent hover:bg-accent/20 transition-colors"
                  >
                    + Add Crew
                  </button>
                </div>
              </div>

              {renderPicker('stayCrew', handleAddStayCrew)}

              {stayRows.length === 0 ? (
                <p className="text-2xs text-text-dim py-2">
                  No crew yet — add editors and assign whole segments; use extra hours for
                  polish, throughlines and other split-out tasks.
                </p>
              ) : (
                <div className="space-y-2">
                  {stayResult.crew.map((m, idx) => {
                    const logged = stayLoggedHours[(m.name || '').toLowerCase()] || 0;
                    return (
                      <div key={idx} className="flex flex-wrap items-center gap-2 p-2 bg-surface rounded border border-border">
                        <UserAvatar name={m.name} color={getUserColor(roster.find(r => parseRosterName(r.name) === m.name) || { name: m.name })} size="sm" />
                        <span className="text-xs font-semibold text-text-primary min-w-0 truncate flex-shrink-0 w-24">
                          {m.name}
                        </span>

                        {/* Rate */}
                        <div className="flex items-center gap-1">
                          <span className="text-2xs text-text-dim">$</span>
                          <input
                            type="number"
                            value={stayRows[idx]?.rate ?? 0}
                            onChange={e => handleStayRowChange(idx, 'rate', e.target.value)}
                            min="0"
                            step="5"
                            className="w-14 px-1.5 py-1 bg-surface-2 border border-border rounded text-2xs text-text-primary text-center focus:outline-none focus:border-accent"
                          />
                          <span className="text-2xs text-text-dim">/hr</span>
                        </div>

                        {/* Segments */}
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={stayRows[idx]?.segments ?? 0}
                            onChange={e => handleStayRowChange(idx, 'segments', e.target.value)}
                            min="0"
                            step="1"
                            className="w-12 px-1.5 py-1 bg-surface-2 border border-border rounded text-2xs text-text-primary text-center focus:outline-none focus:border-accent"
                          />
                          <span className="text-2xs text-text-dim">seg</span>
                        </div>

                        {/* Extra hours */}
                        <div className="flex items-center gap-1">
                          <span className="text-2xs text-text-dim">+</span>
                          <input
                            type="number"
                            value={stayRows[idx]?.extraHours ?? 0}
                            onChange={e => handleStayRowChange(idx, 'extraHours', e.target.value)}
                            min="0"
                            step="0.5"
                            className="w-12 px-1.5 py-1 bg-surface-2 border border-border rounded text-2xs text-text-primary text-center focus:outline-none focus:border-accent"
                          />
                          <span className="text-2xs text-text-dim">h</span>
                        </div>

                        <span className="text-2xs text-text-dim">= {m.hours}h</span>
                        {logged > 0 && (
                          <span className="text-2xs text-text-dim">({logged}h logged)</span>
                        )}

                        {numericAmount > 0 && (
                          <span className="text-xs font-semibold ml-auto text-yellow-500">
                            ~{fmtC(m.earned)}
                          </span>
                        )}

                        <button
                          onClick={() => handleRemoveStayCrew(idx)}
                          className="text-red-accent text-2xs hover:bg-red-accent/10 rounded flex-shrink-0 transition-colors inline-flex items-center justify-center min-w-[40px] min-h-[40px] md:min-w-0 md:min-h-0 md:px-1.5 md:py-0.5"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Over labor pool warning (company target is company-visible) */}
              {canSeeCompany && numericAmount > 0 && stayResult.companyTarget > 0 && stayResult.variance < 0 && (
                <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
                  <p className="text-2xs text-yellow-500 font-semibold">
                    ⚠ {fmtC(-stayResult.variance)} short of the {fmtC(stayResult.companyTarget)} company target
                    — labor exceeds the {fmtC(stayResult.availableForLabor)} pool
                  </p>
                </div>
              )}
              <p className="mt-2 text-2xs text-text-dim">
                Planned hours are estimates — payroll accrues from logged Edit hours at each rate.
              </p>
            </div>
          ) : isItems ? (
            /* ── Per-item leads inside the two department tables ─────── */
            <>
              {renderItemsBanners()}
              {renderItemsDeptCard('prod')}
              {renderItemsDeptCard('post')}
            </>
          ) : (
            <>
              {/* ── Production Section ──────────────────────────────── */}
              {renderDeptSection(
                'prod', 'Production',
                'bg-green', 'text-green',
                prodLead, prodHourly, adjProdResult
              )}

              {/* ── Post Production Section ─────────────────────────── */}
              {renderDeptSection(
                'post', 'Post Production',
                'bg-purple-500', 'text-purple-400',
                postLead, postHourly, adjPostResult
              )}
            </>
          )}

          {/* ── Summary Card ─────────────────────────────────────────── */}
          {numericAmount > 0 && (
            <div className="card p-4">
              <h3 className="text-xs font-semibold text-text-dim uppercase tracking-wide mb-3">
                Earnings Summary
              </h3>

              <div className="space-y-2">
                {/* Company (assigned crew + admins only) */}
                {canSeeCompany && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-accent" />
                      <span className="text-xs text-text-muted">Company</span>
                    </div>
                    <span className="text-xs font-semibold text-accent">{fmtC(companyTotal)}</span>
                  </div>
                )}

                {/* Staycation: variance vs company target */}
                {isStaycation && canSeeCompany && stayResult.companyTarget > 0 && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-surface-3" />
                      <span className="text-xs text-text-muted">
                        vs {fmtC(stayResult.companyTarget)} target ({stayResult.targetPct}%)
                      </span>
                    </div>
                    <span className={cn(
                      'text-xs font-semibold',
                      stayResult.variance >= 0 ? 'text-green' : 'text-yellow-500'
                    )}>
                      {stayResult.variance >= 0
                        ? `+${fmtC(stayResult.variance)} above target`
                        : `${fmtC(-stayResult.variance)} short`}
                    </span>
                  </div>
                )}

                {/* Per-person rows */}
                {perPersonEarnings.map((p, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.hasEstimated ? '#eab308' : getUserColor(roster.find(r => parseRosterName(r.name) === p.name) || { name: p.name }) }} />
                      <span className="text-xs text-text-primary">{p.name}</span>
                      <span className="text-2xs text-text-dim">({p.roles.join(', ')})</span>
                    </div>
                    <span className={`text-xs font-semibold ${p.hasEstimated ? 'text-yellow-500' : 'text-text-primary'}`}>
                      {p.hasEstimated && '~'}{fmtC(p.total)}{p.hasEstimated && ' est'}
                    </span>
                  </div>
                ))}
              </div>

              {/* Job expenses — per-department breakdown */}
              {displayExpenseMeta.totalExpenses > 0 && (
                <div className="mt-1 space-y-0.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500" />
                      <span className="text-xs text-text-muted">Job Expenses</span>
                    </div>
                    <span className="text-xs font-semibold text-orange-400">{fmtC(displayExpenseMeta.totalExpenses)}</span>
                  </div>
                  {displayExpenseMeta.byDept.orig > 0 && (
                    <div className="ml-4 flex items-center justify-between text-2xs text-text-dim">
                      <span>Originator</span>
                      <span>−{fmtC(displayExpenseMeta.absorbed.orig ?? 0)} absorbed{displayExpenseMeta.byDept.orig > (displayExpenseMeta.absorbed.orig ?? 0) ? ` / ${fmtC(displayExpenseMeta.byDept.orig)} total` : ''}</span>
                    </div>
                  )}
                  {displayExpenseMeta.byDept.prod > 0 && (
                    <div className="ml-4 flex items-center justify-between text-2xs text-text-dim">
                      <span>Production</span>
                      <span>−{fmtC(displayExpenseMeta.absorbed.prod ?? 0)} absorbed{displayExpenseMeta.byDept.prod > (displayExpenseMeta.absorbed.prod ?? 0) ? ` / ${fmtC(displayExpenseMeta.byDept.prod)} total` : ''}</span>
                    </div>
                  )}
                  {displayExpenseMeta.byDept.post > 0 && (
                    <div className="ml-4 flex items-center justify-between text-2xs text-text-dim">
                      <span>Post-Production</span>
                      <span>−{fmtC(displayExpenseMeta.absorbed.post ?? 0)} absorbed{displayExpenseMeta.byDept.post > (displayExpenseMeta.absorbed.post ?? 0) ? ` / ${fmtC(displayExpenseMeta.byDept.post)} total` : ''}</span>
                    </div>
                  )}
                </div>
              )}
              {displayExpenseMeta.unabsorbed > 0 && (
                <div className="text-2xs text-orange-300 mt-0.5 ml-4">
                  ⚠ {fmtC(displayExpenseMeta.unabsorbed)} unabsorbed (Company absorbs)
                </div>
              )}

              {/* Overrun note */}
              {displayOverrun > 0 && (
                <div className="mt-2 text-2xs text-red-accent">
                  Overrun absorbed by Company: {fmtC(displayOverrun)}
                </div>
              )}

              {/* Total */}
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                <span className="text-sm font-heading text-text-primary">Total</span>
                <span className="text-lg font-heading text-accent">{fmtC(numericAmount)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Split dialog ── */}
      {splitDialogSid && (() => {
        const line = jobLines.find(l => l.sid === splitDialogSid);
        if (!line) return null;
        return (
          <SplitDialog
            line={line}
            existing={itemSplits[splitDialogSid]}
            onSave={rows => handleSaveSplit(splitDialogSid, rows)}
            onRemove={() => handleRemoveSplit(splitDialogSid)}
            onClose={() => setSplitDialogSid(null)}
          />
        );
      })()}
    </div>
  );
}
